import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { DepotFacade } from '@zero/depot/domain';
import { ChildOnboardingStore, UnderageOnboardingLegalGuardianshipStatus } from '@zero/onboarding/domain';
import { UserNotesComponent } from '@zero/shared/ui-web-components';
import { IconComponent, NotificationComponent } from '@zero/shared/ui-web-design-system';
import { CustomerStateFacade } from '@zero/shared/util-global-state';
import { commonRoutes } from 'constants/web-app-constants';
import { filter, map } from 'rxjs';
import { OnboardingsListComponent } from '../../components/onboardings-list/onboardings-list.component';
import { SituationChecklistComponent } from '../../components/situation-checklist/situation-checklist.component';

@Component({
  templateUrl: './home.component.html',
  imports: [
    OnboardingsListComponent,
    SituationChecklistComponent,
    UserNotesComponent,
    IconComponent,
    TranslocoPipe,
    AsyncPipe,
    NotificationComponent,
  ],
})
export class HomeComponent implements OnInit {
  public readonly store = inject(ChildOnboardingStore);
  public readonly showSituationAndChecklist = signal<boolean>(false);
  public readonly depotFacade = inject(DepotFacade);
  readonly #destroyRef = inject(DestroyRef);

  readonly #router = inject(Router);
  readonly #customerStateFacade = inject(CustomerStateFacade);
  public isAdult$ = this.#customerStateFacade.customerBaseData$.pipe(
    map((customerBaseData) => customerBaseData && !customerBaseData.child),
    takeUntilDestroyed(this.#destroyRef),
  );

  ngOnInit(): void {
    this.#customerStateFacade.loadCustomerBaseData();
    this.depotFacade.loadUserInterests();
    this.store.clear();
    this.store.loadChildOnboardings(true);

    this.#redirectIfNoAdult();
  }

  public add() {
    this.store.resetSelected();
    this.store.loadChildOnboardingSituations();
    this.showSituationAndChecklist.set(true);
  }

  protected continue(onboardingId: string) {
    this.showSituationAndChecklist.set(false);
    this.#router.navigate([commonRoutes.postlogin.child_onboarding.redirect], {
      queryParams: { id: onboardingId },
    });
  }

  protected confirm(onboardingId: string) {
    this.showSituationAndChecklist.set(false);
    this.#router.navigate([commonRoutes.postlogin.child_onboarding.legal_guardian_2_confirm], {
      queryParams: { id: onboardingId },
    });
  }

  protected delete(onboardingId: string) {
    this.store.deleteChildOnboardingById(onboardingId);
  }

  protected start(situationType: UnderageOnboardingLegalGuardianshipStatus) {
    this.store.setSelectedSituation(situationType);
    this.showSituationAndChecklist.set(false);
    this.#router.navigate([commonRoutes.postlogin.child_onboarding.person]);
  }

  #redirectIfNoAdult(): void {
    this.#customerStateFacade.customerBaseData$
      .pipe(
        filter((customerBaseData) => !!customerBaseData?.child),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => this.#router.navigate([commonRoutes.postlogin.member_home]));
  }
}
